import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';

// Layout
import Header from '../layout/header';
import Footer from '../layout/footer';

// Elements
import AvailableCitiesModal from '../elements/available-cities-modal';
import { trackMetaEvent } from '../../utils/metaPixel';

const INVESTMENT_OPTIONS = [
  { value: '1-5', label: '1 Lac - 5 Lac' },
  { value: '5-10', label: '5 Lac - 10 Lac' },
  { value: '10-15', label: '10 Lac - 15 Lac' },
  { value: '15-25', label: '15 Lac - 25 Lac' },
];

const START_OPTIONS = [
  { value: 'next_month', label: 'Next Month' },
  { value: '2_3_months', label: '2 - 3 Months' },
  { value: '3_4_months', label: '3 - 4 Months' },
  { value: 'more_than_4_months', label: 'More than 4 months' },
];

class FranchisePage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      name: '',
      email: '',
      phone: '',
      city: '',
      state: '',
      occupation: '',
      investment: '',
      startWhen: '',
      isSubmitting: false,
      submitError: '',
      submitSuccess: false,
      errors: {},
      touched: {},
    };
    this.ReactPixel = null;
  }

  componentDidMount() {
    trackMetaEvent('FranchisePageView');
  }

  validateEmail = (email) => {
    const emailRegex =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return emailRegex.test(String(email || '').trim());
  };

  validatePhone = (phone) => {
    const phoneRegex = /^(?:(?:\+|0{0,2})91(\s*[\-]\s*)?|[0]?)?[6789]\d{9}$/;
    return phoneRegex.test(String(phone || '').trim());
  };

  handleChange = (event) => {
    const { name, value } = event.target;
    this.setState((prev) => ({
      ...prev,
      [name]: value,
      submitError: '',
      submitSuccess: false,
      errors: { ...prev.errors, [name]: '' },
    }));
  };

  handleBlur = (event) => {
    const { name } = event.target;
    this.setState((prev) => ({ ...prev, touched: { ...prev.touched, [name]: true } }));
  };

  validateForm = () => {
    const errors = {};
    const { name, email, phone, city, state, occupation, investment, startWhen } = this.state;

    if (!String(name).trim()) errors.name = 'Please enter your name';
    if (!this.validateEmail(email)) errors.email = 'Please enter a valid email';
    if (!this.validatePhone(phone)) errors.phone = 'Please enter a valid phone number';
    if (!String(city).trim()) errors.city = 'Please enter your city';
    if (!String(state).trim()) errors.state = 'Please enter your state';
    if (!String(occupation).trim()) errors.occupation = 'Please enter your current occupation';
    if (!investment) errors.investment = 'Please select an investment range';
    if (!startWhen) errors.startWhen = 'Please select when you plan to start';

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  handleSubmit = async (event) => {
    event.preventDefault();
    if (!this.validateForm()) return;

    const { name, email, phone, city, state, occupation, investment, startWhen } = this.state;

    this.setState({ isSubmitting: true, submitError: '', submitSuccess: false });

    try {
      const payload = {
        name: String(name).trim(),
        email: String(email).trim(),
        // send clean 10-digit phone string (backend expects /^\d{10}$/)
        phone: String(phone).replace(/\D/g, '').slice(-10),
        city: String(city).trim(),
        state: String(state).trim(),
        occupation: String(occupation).trim(),
        investment,
        start_when: startWhen,
        source: 'web_franchise_page',
      };

      await axios.post('https://ridenrepair.com/api/add_franchise', payload, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 15000,
      });

      trackMetaEvent('Lead');

      this.setState({
        isSubmitting: false,
        submitSuccess: true,
        submitError: '',
      });
    } catch (error) {
      this.setState({
        isSubmitting: false,
        submitSuccess: false,
        submitError:
          'Something went wrong while submitting your application. Please try again in a moment.',
      });
    }
  };

  render() {
    const { isMobileBrowser, isMobileApp, citiesModal, setCitiesModal, user, setUser } = this.props;
    const canonicalUrl = 'https://www.ridenrepair.com/franchise';
    const {
      name,
      email,
      phone,
      city,
      state,
      occupation,
      investment,
      startWhen,
      isSubmitting,
      submitError,
      submitSuccess,
      errors,
      touched,
    } = this.state;

    const emailOk = this.validateEmail(email);
    const phoneOk = this.validatePhone(phone);
    const canSubmit =
      !isSubmitting &&
      Boolean(name.trim()) &&
      emailOk &&
      phoneOk &&
      Boolean(city.trim()) &&
      Boolean(state.trim()) &&
      Boolean(occupation.trim()) &&
      Boolean(investment) &&
      Boolean(startWhen);

    return (
      <>
        <Helmet>
          <link rel="canonical" href={canonicalUrl} />
          <title>Vehicle Repair Franchise in India | Bike &amp; Car Service | Ride N Repair</title>
          <meta
            name="description"
            content="Apply for a Ride N Repair franchise in India. Start a premium doorstep bike & car service / vehicle repair franchise in your city with training, technology, and trusted operations."
          />
          <meta
            name="keywords"
            content="vehicle repair franchise, vehicle service franchise, two wheeler franchise, bike service franchise, bike repair franchise, car service franchise, car repair franchise, auto repair franchise, doorstep repair franchise, franchise opportunities India, Ride N Repair franchise"
          />
          <meta name="robots" content="index,follow" />
          <meta property="og:title" content="Vehicle Repair Franchise | Ride N Repair" />
          <meta
            property="og:description"
            content="Own a premium doorstep vehicle service business. Apply for a Ride N Repair franchise — bike & car repair, tech-led operations, and luxury customer experience."
          />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:type" content="website" />
          <meta property="og:image" content="https://www.ridenrepair.com/franchise/hero.webp" />
          <meta property="og:image:alt" content="Ride N Repair franchise - premium doorstep bike & car service" />
          <meta property="og:locale" content="en_IN" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Vehicle Repair Franchise | Ride N Repair" />
          <meta
            name="twitter:description"
            content="Apply for a Ride N Repair franchise in India. Start a premium doorstep bike & car service / vehicle repair franchise in your city."
          />
          <meta name="twitter:image" content="https://www.ridenrepair.com/franchise/hero.webp" />
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "What is the Ride N Repair franchise?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Ride N Repair is a premium doorstep vehicle service and repair franchise. Partners run a local operation for bike and car servicing, inspections, repairs, and customer support with training and technology-led workflows."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Is this a two wheeler franchise or car service franchise?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "It’s a vehicle repair franchise designed for both two-wheelers and cars. The model supports bike service and repair as well as car servicing and common repairs based on local demand."
                  }
                },
                {
                  "@type": "Question",
                  "name": "What investment range is required for a Ride N Repair franchise?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Investment depends on your city, setup, staffing, and scale. The application form includes investment ranges to help our team recommend the right launch plan."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How fast can I start a vehicle franchise with Ride N Repair?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Timelines vary by city and readiness. After you apply, our team evaluates your profile and works with you on onboarding, training, and go-live planning."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How do I apply for the franchise?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Fill the franchise application form on this page. Our team will contact you with next steps, eligibility checks, and launch requirements."
                  }
                }
              ]
            })}
          </script>
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "VideoObject",
              "name": "Ride N Repair Franchise",
              "description": "A short introduction to the Ride N Repair franchise opportunity for premium doorstep bike and car service.",
              "thumbnailUrl": ["https://www.ridenrepair.com/franchise/hero.webp"],
              "uploadDate": "2026-01-03",
              "contentUrl": "https://www.ridenrepair.com/franchise/franchise-story.mp4"
            })}
          </script>
        </Helmet>

        <div className="rr-home--lux rr-franchise">
          {citiesModal && <AvailableCitiesModal closeModal={() => setCitiesModal(false)} />}

          <Header
            isMobileBrowser={isMobileBrowser}
            isMobileApp={isMobileApp}
            user={user}
            setUser={setUser}
          />

          <div className="page-content bg-white">
            <section className="rr-franchise-hero">
              <div className="container">
                <div className="rr-franchise-hero__grid">
                <div className="rr-franchise-hero__copy">
                  <p className="rr-kicker">Franchise</p>
                  <h1 className="rr-franchise-hero__title">
                    Build a <span className="rr-accent">Luxury</span> Vehicle Repair Brand in Your City
                  </h1>
                  <p className="rr-franchise-hero__subhead">
                    Ride N Repair is building the future of doorstep car &amp; bike care. If you want to
                    own a premium brand that becomes a legacy — this is your moment.
                  </p>
                  <p className="rr-franchise-hero__subhead rr-franchise-hero__subhead--seo">
                    This is a modern <strong>vehicle repair franchise</strong> built for India — ideal if you’re exploring a
                    <strong> two wheeler franchise</strong>, <strong>bike service franchise</strong>, or <strong>car service franchise</strong> with a luxury customer experience.
                  </p>

                  <div className="rr-franchise-metrics" aria-label="Highlights">
                    <div className="rr-metric">
                      <div className="rr-metric__value">Premium</div>
                      <div className="rr-metric__label">Brand &amp; Experience</div>
                    </div>
                    <div className="rr-metric">
                      <div className="rr-metric__value">Tech-Driven</div>
                      <div className="rr-metric__label">Operations</div>
                    </div>
                    <div className="rr-metric">
                      <div className="rr-metric__value">Doorstep</div>
                      <div className="rr-metric__label">Convenience</div>
                    </div>
                  </div>

                  <div className="rr-franchise-hero__cta">
                    <a className="btn btn-primary" href="#apply">
                      Apply Now
                    </a>
                    <Link className="btn btn-outline-secondary" to="/contact">
                      Talk to Us
                    </Link>
                  </div>
                </div>

                <div className="rr-franchise-hero__media" aria-label="Franchise media">
                  <div className="rr-media-card">
                    <video
                      className="rr-franchise-video"
                      controls
                      muted
                      autoPlay
                      loop
                      playsInline
                      preload="metadata"
                      poster="/franchise/hero.webp"
                    >
                      <source src="/franchise/franchise-story.mp4" type="video/mp4" />
                      Your browser does not support the video tag.{" "}
                      <a href="/franchise/franchise-story.mp4">Download the video</a>.
                    </video>
                    <div className="rr-media-caption">
                      A premium story deserves premium execution — Ride N Repair’s doorstep bike &amp; car service franchise.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

            <section className="rr-franchise-section">
              <div className="container">
                <div className="rr-franchise-split">
                <div className="rr-franchise-content">
                  <div className="heading-bx rr-section-header">
                    <p className="rr-kicker">Why now</p>
                    <h2 className="rr-headline rr-franchise-h2">Why Ride N Repair?</h2>
                    <p className="rr-subhead">
                      We’re building a luxury-grade standard for vehicle care: fast, transparent, and engineered for trust —
                      designed to scale as a <strong>vehicle service franchise</strong> across cities.
                    </p>
                  </div>

                  <div className="rr-franchise-cards">
                    <div className="rr-franchise-card">
                      <h3>Brand that feels premium</h3>
                      <p>Design-led experience, strong identity, and a trust-first customer journey.</p>
                    </div>
                    <div className="rr-franchise-card">
                      <h3>Unit economics + operations</h3>
                      <p>Repeat customers, predictable service demand, and scalable local execution.</p>
                    </div>
                    <div className="rr-franchise-card">
                      <h3>Training + playbooks</h3>
                      <p>Hiring, onboarding, quality checks, and SOPs — built for consistent outcomes.</p>
                    </div>
                    <div className="rr-franchise-card">
                      <h3>Technology backbone</h3>
                      <p>App-led workflows, real-time updates, lead systems, and performance visibility.</p>
                    </div>
                  </div>

                  <div className="rr-franchise-gallery" aria-label="Gallery">
                    <img src="/franchise/gallery-1.webp" alt="Ride N Repair franchise operations" loading="lazy" />
                    <img src="/franchise/gallery-2.webp" alt="Premium vehicle repair experience" loading="lazy" />
                    <img src="/franchise/gallery-3.webp" alt="Doorstep mechanics network" loading="lazy" />
                  </div>

                  <div className="heading-bx rr-section-header" style={{ marginTop: '22px' }}>
                    <p className="rr-kicker">Franchise FAQs</p>
                    <h2 className="rr-headline rr-franchise-h2">Vehicle Franchise Questions, Answered</h2>
                    <p className="rr-subhead">
                      If you’re searching for the best <strong>repair franchise</strong> or <strong>vehicle franchise opportunity</strong>, here are the most common questions we get.
                    </p>
                  </div>

                  <div className="rr-franchise-cards" aria-label="Franchise frequently asked questions">
                    <div className="rr-franchise-card">
                      <h3>What services does this franchise cover?</h3>
                      <p>
                        Doorstep bike service &amp; repair, car servicing, inspections, common maintenance, and support workflows —
                        structured for quality and repeat demand.
                      </p>
                    </div>
                    <div className="rr-franchise-card">
                      <h3>Who is this franchise best for?</h3>
                      <p>
                        Operators and entrepreneurs who want a premium brand, strong SOPs, and a tech-led model — not a typical garage experience.
                      </p>
                    </div>
                    <div className="rr-franchise-card">
                      <h3>How do leads and bookings work?</h3>
                      <p>
                        Customers discover Ride N Repair online and via local demand. The operation runs on structured workflows and a trust-first customer journey.
                      </p>
                    </div>
                    <div className="rr-franchise-card">
                      <h3>How do I apply for the franchise?</h3>
                      <p>
                        Submit the form on this page with your city, investment range, and timeline. Our team reviews your details and shares the next steps.
                      </p>
                    </div>
                  </div>
                </div>

                <aside className="rr-franchise-formWrap" id="apply" aria-label="Apply for franchise">
                  <div className="rr-formCard">
                    <h2 className="rr-formTitle">Apply For Franchise</h2>

                    <form onSubmit={this.handleSubmit} className="rr-form">
                      <div className="rr-field">
                        <input
                          name="name"
                          value={name}
                          onChange={this.handleChange}
                          onBlur={this.handleBlur}
                          placeholder="Name"
                          className="rr-input"
                          required
                        />
                        {(errors.name || (touched.name && !name.trim() && 'Please enter your name')) && (
                          <div className="rr-error">{errors.name || 'Please enter your name'}</div>
                        )}
                      </div>

                      <div className="rr-row">
                        <div className="rr-field">
                          <input
                            name="email"
                            type="email"
                            value={email}
                            onChange={this.handleChange}
                            onBlur={this.handleBlur}
                            placeholder="Email"
                            className="rr-input"
                            required
                          />
                          {(errors.email || (touched.email && !emailOk && 'Please enter a valid email')) && (
                            <div className="rr-error">{errors.email || 'Please enter a valid email'}</div>
                          )}
                        </div>
                        <div className="rr-field">
                          <input
                            name="phone"
                            type="tel"
                            inputMode="tel"
                            autoComplete="tel"
                            value={phone}
                            onChange={this.handleChange}
                            onBlur={this.handleBlur}
                            placeholder="Phone Number"
                            className="rr-input"
                            required
                          />
                          {(errors.phone || (touched.phone && !phoneOk && 'Please enter a valid phone number')) && (
                            <div className="rr-error">{errors.phone || 'Please enter a valid phone number'}</div>
                          )}
                        </div>
                      </div>

                      <div className="rr-row">
                        <div className="rr-field">
                          <input
                            name="city"
                            value={city}
                            onChange={this.handleChange}
                            onBlur={this.handleBlur}
                            placeholder="City"
                            className="rr-input"
                            required
                          />
                          {(errors.city || (touched.city && !city.trim() && 'Please enter your city')) && (
                            <div className="rr-error">{errors.city || 'Please enter your city'}</div>
                          )}
                        </div>
                        <div className="rr-field">
                          <input
                            name="state"
                            value={state}
                            onChange={this.handleChange}
                            onBlur={this.handleBlur}
                            placeholder="State"
                            className="rr-input"
                            required
                          />
                          {(errors.state || (touched.state && !state.trim() && 'Please enter your state')) && (
                            <div className="rr-error">{errors.state || 'Please enter your state'}</div>
                          )}
                        </div>
                      </div>

                      <div className="rr-field">
                        <input
                          name="occupation"
                          value={occupation}
                          onChange={this.handleChange}
                          onBlur={this.handleBlur}
                          placeholder="Current Occupation"
                          className="rr-input"
                          required
                        />
                        {(errors.occupation ||
                          (touched.occupation && !occupation.trim() && 'Please enter your current occupation')) && (
                          <div className="rr-error">{errors.occupation || 'Please enter your current occupation'}</div>
                        )}
                      </div>

                      <div className="rr-field">
                        <select
                          name="investment"
                          value={investment}
                          onChange={this.handleChange}
                          onBlur={this.handleBlur}
                          className="rr-select"
                          required
                        >
                          <option value="">Select Investment</option>
                          {INVESTMENT_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.label}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        {(errors.investment || (touched.investment && !investment && 'Please select an investment range')) && (
                          <div className="rr-error">{errors.investment || 'Please select an investment range'}</div>
                        )}
                      </div>

                      <div className="rr-field">
                        <select
                          name="startWhen"
                          value={startWhen}
                          onChange={this.handleChange}
                          onBlur={this.handleBlur}
                          className="rr-select"
                          required
                        >
                          <option value="">When are you planning to start?</option>
                          {START_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.label}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        {(errors.startWhen || (touched.startWhen && !startWhen && 'Please select when you plan to start')) && (
                          <div className="rr-error">{errors.startWhen || 'Please select when you plan to start'}</div>
                        )}
                      </div>

                      {submitError && <div className="rr-submitError">{submitError}</div>}
                      {submitSuccess && (
                        <div className="rr-submitSuccess">
                          Submitted successfully. Our team will contact you shortly.
                        </div>
                      )}

                      <button className="btn btn-primary rr-submit" type="submit" disabled={!canSubmit}>
                        {isSubmitting ? 'Submitting…' : 'Apply Now'}
                      </button>

                      <div className="rr-formFoot">
                        By sharing your details, you agree to our{' '}
                        <Link to="/privacy-policy">Privacy Policy</Link>
                      </div>
                    </form>
                  </div>
                </aside>
              </div>
            </div>
          </section>

          <section className="rr-franchise-section rr-franchise-section--cta">
            <div className="container">
              <div className="rr-finalCta">
                <div>
                  <p className="rr-kicker">Legacy</p>
                  <h2 className="rr-headline rr-finalCta__title">Own a brand your city will remember.</h2>
                  <p className="rr-subhead rr-finalCta__sub">
                    Premium experience. Trusted technicians. Tech-led operations. A franchise designed to scale.
                  </p>
                </div>
                <div className="rr-finalCta__actions">
                  <a className="btn btn-primary" href="#apply">
                    Apply Now
                  </a>
                  <a className="btn btn-outline-secondary" href="tel:+911203615050">
                    Call +911203615050
                  </a>
                </div>
              </div>
            </div>
          </section>
          </div>

          <Footer setCitiesModal={setCitiesModal} isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} />
        </div>
      </>
    );
  }
}

export default FranchisePage;


